// Leer un PDF con pdf.js — ADR-2026-09-08-el-agente-lee-y-crea-pdfs.
//
// Acá SÍ entra una librería, al revés que con el xlsx y que con la escritura de PDF. Un
// PDF guarda el texto como glifos posicionados, con streams comprimidos, codificaciones de
// fuente variadas y tablas ToUnicode. Un extractor casero anda con los PDFs de prueba y
// devuelve MOJIBAKE con la mitad de los reales: no falla, miente. `pdfjs-dist` es de
// Mozilla y es la implementación de referencia.
//
// Entra con `import()` DINÁMICO: pesa ~1MB y no tiene por qué estar en el bundle principal
// de una app donde la mayoría nunca adjunta un PDF.
//
// Dos clases de PDF, y la segunda es la mitad de los que la gente tiene:
//   • Con capa de texto (exportado de Word, Excel, un sistema de facturación) → se extrae.
//   • ESCANEADO o fotografiado (una factura del proveedor, un contrato firmado) → no tiene
//     texto, es una imagen adentro de un PDF. Ahí se RENDERIZAN las páginas y el agente las
//     mira como imágenes.

export interface PdfLeido {
  paginas: number;
  /** Texto por página, en orden. Vacío donde la página no tenía capa de texto. */
  textoPorPagina: string[];
  /** false = escaneado (o casi): hay que mirarlo como imagen. */
  tieneTexto: boolean;
}

/** Debajo de esto tratamos el PDF como escaneado: un par de sellos sueltos no es texto. */
const MIN_CARACTERES_PARA_SER_TEXTO = 40;

type Pdfjs = typeof import('pdfjs-dist');

let pdfjsCache: Promise<Pdfjs> | null = null;

/** Carga pdf.js una sola vez y le apunta el worker. */
function cargarPdfjs(): Promise<Pdfjs> {
  if (!pdfjsCache) {
    pdfjsCache = import('pdfjs-dist').then((mod) => {
      // El worker se resuelve por URL desde el propio paquete: así Vite lo emite como
      // asset aparte y no hay que copiarlo a mano a `public/`.
      mod.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();
      return mod;
    });
  }
  return pdfjsCache;
}

async function abrir(bytes: Uint8Array) {
  const pdfjs = await cargarPdfjs();
  try {
    // `bytes.slice()`: pdf.js se queda con el buffer y lo deja detached, y el mismo
    // Uint8Array se vuelve a usar si el usuario pide renderizar después.
    return await pdfjs.getDocument({ data: bytes.slice() }).promise;
  } catch {
    throw new Error('No pude abrir ese PDF. ¿Está completo y sin contraseña?');
  }
}

/** Texto de todas las páginas (hasta `maxPaginas`). */
export async function leerPdf(bytes: Uint8Array, maxPaginas = 200): Promise<PdfLeido> {
  const doc = await abrir(bytes);
  const total = Math.min(doc.numPages, maxPaginas);
  const textoPorPagina: string[] = [];
  for (let i = 1; i <= total; i++) {
    try {
      const pagina = await doc.getPage(i);
      const contenido = await pagina.getTextContent();
      // Los items vienen como fragmentos posicionados; `hasEOL` marca el fin de renglón.
      const texto = contenido.items
        .map((it) => {
          const item = it as { str?: string; hasEOL?: boolean };
          return (item.str ?? '') + (item.hasEOL ? '\n' : '');
        })
        .join('')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      textoPorPagina.push(texto);
    } catch {
      textoPorPagina.push('');
    }
  }
  const caracteres = textoPorPagina.reduce((n, t) => n + t.length, 0);
  return {
    paginas: doc.numPages,
    textoPorPagina,
    tieneTexto: caracteres >= MIN_CARACTERES_PARA_SER_TEXTO,
  };
}

/**
 * Renderiza páginas a data URL (JPEG). Es el camino de los ESCANEADOS: entran por los
 * bloques de imagen que el chat ya sabe mandar.
 *
 * `escala` acota el tamaño: una página A4 a escala 1.5 son ~1240px de ancho, suficiente
 * para que el modelo lea un texto impreso sin que el pedido pese de más.
 */
export async function renderizarPaginas(
  bytes: Uint8Array,
  desde: number,
  cantidad: number,
  escala = 1.5,
): Promise<string[]> {
  const doc = await abrir(bytes);
  const out: string[] = [];
  const primera = Math.max(1, Math.floor(desde));
  const ultima = Math.min(doc.numPages, primera + Math.max(1, Math.floor(cantidad)) - 1);
  for (let i = primera; i <= ultima; i++) {
    try {
      const pagina = await doc.getPage(i);
      const viewport = pagina.getViewport({ scale: escala });
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      // Fondo blanco: un PDF sin fondo se renderiza transparente y al pasarlo a JPEG
      // queda negro.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await pagina.render({ canvas, canvasContext: ctx, viewport }).promise;
      out.push(canvas.toDataURL('image/jpeg', 0.82));
    } catch {
      /* una página que no renderiza se saltea; el resto sirve igual */
    }
  }
  return out;
}

/** Una foto embebida en un PDF, lista para subir (ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf). */
export interface FotoDelPdf {
  pagina: number;
  ancho: number;
  alto: number;
  blob: Blob;
}

export interface FotosExtraidas {
  fotos: FotoDelPdf[];
  /** Imágenes que no entraron: chicas (logos, íconos), repetidas en otras páginas o por el tope. */
  descartadas: number;
}

/** Fotos por llamada: un informe de inspección de 21 páginas trae ~100 (con los bocetos de la caja). */
export const MAX_FOTOS_PDF = 100;
/** Debajo de este lado no es una foto: un logo del encabezado, un ícono, una viñeta. */
export const MIN_LADO_FOTO_PDF = 200;
/** Lado máximo con el que se sube: más no le sirve a ningún motor como referencia. */
const MAX_LADO_FOTO_PDF = 1600;

type ImagenDePdfjs = { width?: number; height?: number; bitmap?: CanvasImageSource; data?: Uint8ClampedArray; kind?: number };
type ObjetosDePdfjs = { get: (id: string, cb: (o: unknown) => void) => void };

/** El objeto de una imagen de la página. `g_…` vive en los objetos comunes (se repite entre páginas). */
function objetoDeImagen(pagina: unknown, id: string): Promise<ImagenDePdfjs | null> {
  const p = pagina as { objs: ObjetosDePdfjs; commonObjs: ObjetosDePdfjs };
  const objetos = id.startsWith('g_') ? p.commonObjs : p.objs;
  return new Promise((resolve) => {
    const corte = setTimeout(() => resolve(null), 5_000);
    try {
      objetos.get(id, (o) => {
        clearTimeout(corte);
        resolve((o as ImagenDePdfjs) ?? null);
      });
    } catch {
      clearTimeout(corte);
      resolve(null);
    }
  });
}

/** La imagen de pdf.js dibujada en un canvas (bitmap en el navegador; si no, los píxeles crudos). */
function aCanvas(img: ImagenDePdfjs, ancho: number, alto: number, kindRgba: number, kindRgb: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  if (img.bitmap) {
    ctx.drawImage(img.bitmap, 0, 0);
    return canvas;
  }
  const src = img.data;
  if (!src) return null;
  const destino = ctx.createImageData(ancho, alto);
  if (img.kind === kindRgba && src.length >= ancho * alto * 4) destino.data.set(src.subarray(0, ancho * alto * 4));
  else if (img.kind === kindRgb && src.length >= ancho * alto * 3) {
    for (let p = 0, q = 0; p < ancho * alto; p++) {
      destino.data[q++] = src[p * 3];
      destino.data[q++] = src[p * 3 + 1];
      destino.data[q++] = src[p * 3 + 2];
      destino.data[q++] = 255;
    }
  } else return null; // máscaras de 1 bit: no son fotos
  ctx.putImageData(destino, 0, 0);
  return canvas;
}

/**
 * Huella de 16×16 en 16 grises: la misma foto puesta en dos páginas da la misma huella. Más fina
 * que un hash de 8×8 a propósito: en un informe de fábrica las vistas del producto (frente, costado,
 * atrás, sobre el mismo fondo) daban la misma huella de 8×8 y se perdían como "repetidas".
 */
function huella(canvas: HTMLCanvasElement): string {
  const chico = document.createElement('canvas');
  chico.width = 16;
  chico.height = 16;
  const ctx = chico.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(canvas, 0, 0, 16, 16);
  const d = ctx.getImageData(0, 0, 16, 16).data;
  let out = '';
  for (let i = 0; i < d.length; i += 4) out += Math.floor((d[i] + d[i + 1] + d[i + 2]) / 3 / 16).toString(16);
  return out;
}

function aJpeg(canvas: HTMLCanvasElement): Promise<{ blob: Blob; ancho: number; alto: number } | null> {
  let fuente = canvas;
  const lado = Math.max(canvas.width, canvas.height);
  if (lado > MAX_LADO_FOTO_PDF) {
    const k = MAX_LADO_FOTO_PDF / lado;
    fuente = document.createElement('canvas');
    fuente.width = Math.round(canvas.width * k);
    fuente.height = Math.round(canvas.height * k);
    fuente.getContext('2d')?.drawImage(canvas, 0, 0, fuente.width, fuente.height);
  }
  return new Promise((resolve) =>
    fuente.toBlob((b) => resolve(b ? { blob: b, ancho: fuente.width, alto: fuente.height } : null), 'image/jpeg', 0.88),
  );
}

/**
 * Las FOTOS embebidas en un PDF (no las páginas renderizadas): una ficha técnica o un informe de
 * inspección de fábrica trae el producto fotografiado adentro. Se saltean las chicas (logos e
 * íconos), las que se repiten en varias páginas y las que pasan de `maxFotos`.
 * ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf.
 */
export async function extraerFotosDelPdf(
  bytes: Uint8Array,
  opciones: { paginas?: number[]; maxFotos?: number } = {},
): Promise<FotosExtraidas> {
  const pdfjs = await cargarPdfjs();
  const doc = await abrir(bytes);
  const maxFotos = opciones.maxFotos ?? MAX_FOTOS_PDF;
  const paginas = (opciones.paginas?.length ? opciones.paginas : Array.from({ length: doc.numPages }, (_, i) => i + 1))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= doc.numPages);
  const pintar = new Set([pdfjs.OPS.paintImageXObject, pdfjs.OPS.paintImageXObjectRepeat, pdfjs.OPS.paintInlineImageXObject]);
  const vistos = new Set<string>();
  const huellas = new Set<string>();
  const fotos: FotoDelPdf[] = [];
  let descartadas = 0;
  try {
    for (const n of paginas) {
      let pagina;
      let ops;
      try {
        pagina = await doc.getPage(n);
        ops = await pagina.getOperatorList();
      } catch {
        continue; // una página rota no corta el resto
      }
      for (let i = 0; i < ops.fnArray.length; i++) {
        if (!pintar.has(ops.fnArray[i])) continue;
        const arg = (ops.argsArray[i] as unknown[])[0];
        const id = typeof arg === 'string' ? arg : null;
        if (id && vistos.has(id)) {
          descartadas++;
          continue;
        }
        if (id) vistos.add(id);
        const img = id ? await objetoDeImagen(pagina, id) : (arg as ImagenDePdfjs);
        const ancho = img?.width ?? 0;
        const alto = img?.height ?? 0;
        if (!img || Math.min(ancho, alto) < MIN_LADO_FOTO_PDF || fotos.length >= maxFotos) {
          descartadas++;
          continue;
        }
        const canvas = aCanvas(img, ancho, alto, pdfjs.ImageKind.RGBA_32BPP, pdfjs.ImageKind.RGB_24BPP);
        const h = canvas ? huella(canvas) : '';
        if (!canvas || !h || huellas.has(h)) {
          descartadas++;
          continue;
        }
        huellas.add(h);
        const jpeg = await aJpeg(canvas);
        if (jpeg) fotos.push({ pagina: n, ...jpeg });
        else descartadas++;
      }
    }
  } finally {
    // Libera lo que pdf.js guardó de las páginas (las fotos ya se copiaron a JPEG).
    void doc.cleanup();
  }
  return { fotos, descartadas };
}

/**
 * Hojas de contacto: grillas numeradas de miniaturas, para que el agente vea muchas fotos en pocas
 * imágenes y elija por número. `numeros[i]` es el número que se dibuja sobre `fotos[i]`.
 */
export async function hojasDeContacto(
  fotos: Array<{ blob: Blob; pagina: number }>,
  numeros: number[],
  porHoja = 16,
): Promise<string[]> {
  const COLUMNAS = 4;
  const CELDA_ANCHO = 300;
  const CELDA_ALTO = 225;
  const MARGEN = 8;
  const hojas: string[] = [];
  for (let desde = 0; desde < fotos.length; desde += porHoja) {
    const tramo = fotos.slice(desde, desde + porHoja);
    const filas = Math.ceil(tramo.length / COLUMNAS);
    const canvas = document.createElement('canvas');
    canvas.width = COLUMNAS * (CELDA_ANCHO + MARGEN) + MARGEN;
    canvas.height = filas * (CELDA_ALTO + MARGEN) + MARGEN;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for (let k = 0; k < tramo.length; k++) {
      const x = MARGEN + (k % COLUMNAS) * (CELDA_ANCHO + MARGEN);
      const y = MARGEN + Math.floor(k / COLUMNAS) * (CELDA_ALTO + MARGEN);
      try {
        const bmp = await createImageBitmap(tramo[k].blob);
        const escala = Math.min(CELDA_ANCHO / bmp.width, CELDA_ALTO / bmp.height);
        const w = bmp.width * escala;
        const h = bmp.height * escala;
        ctx.drawImage(bmp, x + (CELDA_ANCHO - w) / 2, y + (CELDA_ALTO - h) / 2, w, h);
        bmp.close();
      } catch {
        /* una miniatura que no se dibuja deja la celda en blanco con su número */
      }
      const etiqueta = `${numeros[desde + k]} · p${tramo[k].pagina}`;
      ctx.font = 'bold 20px sans-serif';
      const ancho = ctx.measureText(etiqueta).width + 16;
      ctx.fillStyle = 'rgba(2, 6, 43, 0.85)';
      ctx.fillRect(x, y, ancho, 30);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(etiqueta, x + 8, y + 22);
    }
    hojas.push(canvas.toDataURL('image/jpeg', 0.8));
  }
  return hojas;
}
